/* /m/:handle/:slug — public view of one of a member's product mirror sites.
   If the mirror has a WYSIWYG `blocks` array, render those. Otherwise fall
   back to the legacy structured layout (free gift / upsell / bump). */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ExternalLink, Mail, Gift, Check } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const BRAND = "#287F8E";
const GREEN = "#2E9F3A";
const ORANGE = "#F39A1F";
const WINE = "#8B2A2A";

function resolveSrc(src) {
  if (!src) return src;
  if (src.startsWith("/api/")) return `${BACKEND}${src}`;
  return src;
}

function extractYouTubeId(u) {
  const url = (u || "").trim();
  if (!url) return null;
  const re = /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/))([A-Za-z0-9_-]{11})/;
  const match = url.match(re);
  return match ? match[1] : null;
}

export default function MirrorSiteProduct() {
  const { handle, slug } = useParams();
  const [m, setM] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (handle) {
      try { window.localStorage.setItem("aff", handle); } catch (_) {}
    }
    axios.get(`${BACKEND}/api/mirrors/public/${handle}/${slug}`)
      .then((r) => setM(r.data?.mirror || null))
      .catch(() => setError("This mirror site doesn't exist or has been unpublished."));
  }, [handle, slug]);

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6 text-center">
        <div>
          <h1 className="font-serif-display text-3xl uppercase mb-2" style={{ color: "#0F172A" }}>{error}</h1>
          <Link to="/" className="underline" style={{ color: BRAND }}>Go to faitharama.com →</Link>
        </div>
      </div>
    );
  }
  if (!m) {
    return <div className="min-h-screen flex items-center justify-center text-foreground/60 text-sm">Loading…</div>;
  }

  const bg = m.bg_color || "#FFFFFF";
  const textColor = m.text_color || "#0F172A";
  const hasBlocks = Array.isArray(m.blocks) && m.blocks.length > 0;

  return (
    <div className="min-h-screen" style={{ background: bg, color: textColor }} data-testid="mirror-product-page">
      <header className="border-b py-4 px-4 flex items-center justify-between" style={{ background: "#EAF6FC", borderColor: `${BRAND}33` }}>
        <Link to="/" className="font-serif-display text-xl uppercase" style={{ color: "#0F172A" }}>FAITHARAMA<span style={{ color: GREEN }}>.com</span></Link>
      </header>

      {hasBlocks ? (
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-10">
          {m.blocks.map((b) => (
            <PublicBlock key={b.id} block={b} mirror={m}/>
          ))}
        </div>
      ) : (
        <LegacyMirror m={m} handle={handle} slug={slug}/>
      )}

      <footer className="border-t-2 py-6 px-4 text-center" style={{ background: "#EAF6FC", borderColor: ORANGE }}>
        <Link to="/" className="font-serif-display text-lg uppercase" style={{ color: "#0F172A" }}>
          ★ FAITHARAMA.COM — Visit The Main Site
        </Link>
      </footer>
    </div>
  );
}

/* ------------ PUBLIC BLOCK RENDERER ------------ */
function PublicBlock({ block, mirror }) {
  const wrap = (children) => (
    <div style={{ paddingTop: block.padY ?? 8, paddingBottom: block.padY ?? 8 }}>{children}</div>
  );

  switch (block.type) {
    case "heading":
      return wrap(
        <h1 style={{
          fontSize: block.fontSize, color: block.color, textAlign: block.align,
          fontFamily: block.fontFamily === "sans" ? "ui-sans-serif, system-ui, sans-serif" : "Georgia, 'Times New Roman', serif",
          fontWeight: block.bold ? 800 : 600, lineHeight: 1.05,
          textTransform: block.uppercase ? "uppercase" : "none", margin: 0,
        }}>{block.text}</h1>
      );
    case "text":
      return wrap(
        <p style={{
          fontSize: block.fontSize, color: block.color, textAlign: block.align,
          fontFamily: block.fontFamily === "sans" ? "ui-sans-serif, system-ui, sans-serif" : "Georgia, 'Times New Roman', serif",
          lineHeight: 1.5, margin: 0,
        }}>{block.text}</p>
      );
    case "image":
      return wrap(
        <div style={{ textAlign: block.align || "center" }}>
          {block.src && (
            <img src={resolveSrc(block.src)} alt=""
                 style={{ width: `${block.widthPct || 60}%`, borderRadius: block.radius || 0, display: "inline-block", maxWidth: "100%" }}/>
          )}
        </div>
      );
    case "video": {
      const ytId = extractYouTubeId(block.url);
      if (!ytId) return null;
      return wrap(
        <div className="mx-auto rounded-lg overflow-hidden shadow bg-black" style={{ aspectRatio: "16/9", maxWidth: 720 }}>
          <iframe src={`https://www.youtube.com/embed/${ytId}?rel=0`} title="Video"
                  className="w-full h-full" style={{ border: 0 }} allowFullScreen/>
        </div>
      );
    }
    case "button":
      return wrap(
        <div style={{ textAlign: block.align || "center" }}>
          {block.url ? (
            <a href={block.url} target={block.openInNew ? "_blank" : "_self"} rel="noopener noreferrer"
               className="inline-flex items-center justify-center px-7 py-3.5 rounded-md font-bold uppercase tracking-[0.18em] shadow"
               style={{ background: block.bgColor, color: block.textColor, fontSize: block.fontSize }}>
              {block.label}
            </a>
          ) : (
            <span className="inline-flex items-center justify-center px-7 py-3.5 rounded-md font-bold uppercase tracking-[0.18em] shadow opacity-70"
                  style={{ background: block.bgColor, color: block.textColor, fontSize: block.fontSize }}>
              {block.label}
            </span>
          )}
        </div>
      );
    case "spacer":
      return <div style={{ height: block.height || 40 }}/>;
    case "divider":
      return wrap(<hr style={{ borderTop: `${block.thickness || 2}px solid ${block.color || "#E5E7EB"}`, border: 0, borderTopStyle: "solid" }}/>);
    case "columns":
      return wrap(<PublicColumns block={block} mirror={mirror}/>);
    case "form":
      return wrap(<PublicForm block={block} mirror={mirror}/>);
    case "testimonial":
      return wrap(
        <div className="rounded-xl p-5 md:p-7 border-2"
             style={{ background: block.bgColor || "#FFFBEF", color: block.textColor || "#0F172A", borderColor: `${ORANGE}55` }}>
          <div className="flex items-start gap-4 flex-wrap">
            {block.photoUrl ? (
              <img src={resolveSrc(block.photoUrl)} alt="" className="w-16 h-16 rounded-full object-cover border-2" style={{ borderColor: ORANGE }}/>
            ) : null}
            <div className="flex-1 min-w-[200px]">
              <div style={{ fontFamily: "Georgia, serif", fontSize: 20, fontStyle: "italic", lineHeight: 1.4, marginBottom: 8 }}>{block.quote}</div>
              <div className="text-sm">
                <strong>{block.author}</strong>
                {block.role ? <span> · <span style={{ opacity: 0.8 }}>{block.role}</span></span> : null}
              </div>
            </div>
          </div>
        </div>
      );
    case "faq":
      return wrap(
        <div className="space-y-2">
          {(block.items || []).map((it, i) => (
            <details key={i} className="rounded-md border bg-white px-4 py-3" style={{ borderColor: "#E2E8F0" }}>
              <summary className="cursor-pointer font-bold" style={{ color: "#0F172A" }}>{it.q}</summary>
              <div className="mt-2 text-sm" style={{ color: "#334155" }}>{it.a}</div>
            </details>
          ))}
        </div>
      );
    default:
      return null;
  }
}

function PublicColumns({ block, mirror }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${block.cols || 2}, 1fr)`, gap: block.gap || 24 }}
         className="public-cols">
      {(block.columns || []).map((col, i) => (
        <div key={i}>
          {(col || []).map((c) => <PublicBlock key={c.id} block={c} mirror={mirror}/>)}
        </div>
      ))}
    </div>
  );
}

function PublicForm({ block, mirror }) { // eslint-disable-line no-unused-vars
  const { handle, slug } = useParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Email is required"); return; }
    try {
      await axios.post(`${BACKEND}/api/leads`, {
        email: email.trim(),
        name: name.trim() || null,
        source: `mirror:${handle}/${slug}`,
        ref: handle,
      });
    } catch (_) { /* still show success */ }
    setSent(true);
    toast.success("★ On its way!");
  };

  const showName = (block.fields || ["name","email"]).includes("name");
  const accent = block.accentColor || ORANGE;

  return (
    <div className="rounded-xl border-2 shadow-md p-5 md:p-7"
         style={{ background: block.bgColor || "#EAF6FC", color: block.textColor || "#0F172A", borderColor: `${accent}55` }}>
      <h3 style={{ fontFamily: "Georgia, serif", fontSize: 28, fontWeight: 800, textTransform: "uppercase", margin: 0, marginBottom: 8 }}>{block.title}</h3>
      {block.description && <p style={{ fontSize: 15, opacity: 0.85, margin: 0, marginBottom: 14 }}>{block.description}</p>}
      {sent ? (
        <div className="rounded-md p-4 text-center" style={{ background: `${GREEN}15`, border: `2px solid ${GREEN}` }}>
          <Check className="w-8 h-8 mx-auto mb-1" style={{ color: GREEN }}/>
          <div className="font-bold" style={{ color: "#0F172A" }}>★ On its way! Check your inbox.</div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-2" data-testid="public-form">
          {showName && (
            <input type="text" placeholder="Your first name" value={name} onChange={(e) => setName(e.target.value)}
                   data-testid="public-form-name"
                   className="w-full px-4 py-3 rounded-md border-2 text-sm bg-white outline-none focus:ring-2 focus:ring-[#F39A1F]"
                   style={{ borderColor: `${accent}55` }}/>
          )}
          <input type="email" required placeholder="Your email" value={email} onChange={(e) => setEmail(e.target.value)}
                 data-testid="public-form-email"
                 className="w-full px-4 py-3 rounded-md border-2 text-sm bg-white outline-none focus:ring-2 focus:ring-[#F39A1F]"
                 style={{ borderColor: `${accent}55` }}/>
          <button type="submit"
                  data-testid="public-form-submit"
                  className="w-full px-6 py-3.5 rounded-md text-base font-bold uppercase tracking-[0.18em] text-white shadow"
                  style={{ background: accent }}>
            <Mail className="w-4 h-4 inline mr-2"/> {block.buttonLabel || "Send It To Me"}
          </button>
        </form>
      )}
    </div>
  );
}

/* ------------ LEGACY MIRROR (for old mirrors without blocks) ------------ */
function LegacyMirror({ m, handle, slug }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [bumpChecked, setBumpChecked] = useState(false);
  const [sent, setSent] = useState(false);

  const accent = m.accent_color || GREEN;
  const textColor = m.text_color || "#0F172A";
  const font = m.font_family === "sans" ? "ui-sans-serif, system-ui, sans-serif"
             : "Georgia, 'Times New Roman', serif";
  const headlineFont = m.font_family === "sans" ? font : "Georgia, 'Times New Roman', serif";
  const headlineSize = m.headline_size === "lg" ? "clamp(2.5rem, 6vw, 5rem)"
                     : m.headline_size === "sm" ? "clamp(1.5rem, 3.5vw, 2.5rem)"
                     : "clamp(2rem, 5vw, 4rem)";

  const submitFreeGift = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Email is required"); return; }
    try {
      await axios.post(`${BACKEND}/api/leads`, {
        email: email.trim(), name: name.trim() || null,
        source: `mirror:${handle}/${slug}`, ref: handle,
      });
    } catch (_) {}
    setSent(true);
    toast.success("★ On its way!");
  };

  const ytId = extractYouTubeId(m.video_url);

  return (
    <div style={{ fontFamily: font, color: textColor }}>
      <section className="max-w-4xl mx-auto px-4 py-10 md:py-16 text-center">
        {m.photo_url && (
          <img src={m.photo_url} alt="" className="w-32 h-32 md:w-40 md:h-40 mx-auto rounded-full object-cover border-4 shadow-lg mb-6"
               style={{ borderColor: ORANGE }}/>
        )}
        {m.headline && (
          <h1 className="uppercase leading-[1.05] mb-3 font-bold"
              style={{ fontSize: headlineSize, color: textColor, fontFamily: headlineFont, textWrap: "balance" }}>{m.headline}</h1>
        )}
        {m.tagline && <p className="text-base md:text-xl max-w-3xl mx-auto" style={{ opacity: 0.85 }}>{m.tagline}</p>}
        {ytId && (
          <div className="mt-8 max-w-3xl mx-auto rounded-xl overflow-hidden shadow-xl bg-black"
               style={{ aspectRatio: "16 / 9" }}>
            <iframe src={`https://www.youtube.com/embed/${ytId}?rel=0`} title="Video"
                    className="w-full h-full" style={{ border: 0 }} allowFullScreen/>
          </div>
        )}
      </section>

      {m.free_gift_title && (
        <section className="border-t border-b py-12 md:py-16 px-4" style={{ background: "#EAF6FC", borderColor: `${BRAND}33` }}>
          <div className="max-w-2xl mx-auto rounded-2xl bg-white border-2 shadow-xl p-6 md:p-10" style={{ borderColor: ORANGE }}>
            <div className="text-[11px] tracking-[0.32em] uppercase font-bold mb-2 flex items-center gap-2" style={{ color: ORANGE }}>
              <Gift className="w-4 h-4"/> ★ FREE GIFT
            </div>
            <h2 className="font-serif-display text-2xl md:text-4xl uppercase leading-tight mb-3" style={{ color: WINE }}>{m.free_gift_title}</h2>
            {m.free_gift_description && <p className="text-base text-foreground/80 mb-5">{m.free_gift_description}</p>}
            {sent ? (
              <div className="rounded-md p-4 text-center" style={{ background: `${GREEN}15`, border: `2px solid ${GREEN}` }}>
                <Check className="w-8 h-8 mx-auto mb-1" style={{ color: GREEN }}/>
                <div className="font-bold" style={{ color: "#0F172A" }}>★ On its way! Check your inbox.</div>
              </div>
            ) : (
              <form onSubmit={submitFreeGift} className="space-y-3">
                <input type="text" placeholder="Your first name" value={name} onChange={(e) => setName(e.target.value)}
                       className="w-full px-4 py-3 rounded-md border-2 text-base outline-none focus:ring-2 focus:ring-[#F39A1F]"
                       style={{ borderColor: `${ORANGE}55` }}/>
                <input type="email" placeholder="Your email" value={email} onChange={(e) => setEmail(e.target.value)} required
                       className="w-full px-4 py-3 rounded-md border-2 text-base outline-none focus:ring-2 focus:ring-[#F39A1F]"
                       style={{ borderColor: `${ORANGE}55` }}/>
                <button type="submit"
                        className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-md text-base font-bold uppercase tracking-[0.18em] text-white shadow"
                        style={{ background: ORANGE }}>
                  <Mail className="w-4 h-4"/> {m.free_gift_cta || "Send Me The Guide"}
                </button>
              </form>
            )}
          </div>
        </section>
      )}

      {m.upsell_title && (
        <section className="py-12 md:py-16 px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-[11px] tracking-[0.32em] uppercase font-bold mb-2 text-center" style={{ color: GREEN }}>★ THE PRODUCT</div>
            <h2 className="font-serif-display text-2xl md:text-4xl uppercase leading-tight mb-3 text-center" style={{ color: "#0F172A" }}>{m.upsell_title}</h2>
            {m.upsell_description && <p className="text-base text-foreground/80 mb-5 text-center">{m.upsell_description}</p>}
            {m.bump_title && (
              <label className="block rounded-xl border-2 border-dashed p-4 my-5 cursor-pointer hover:bg-[#FFFBEF] transition" style={{ borderColor: WINE }}>
                <div className="flex items-start gap-3">
                  <input type="checkbox" checked={bumpChecked} onChange={(e) => setBumpChecked(e.target.checked)} className="w-5 h-5 mt-0.5 shrink-0"/>
                  <div>
                    <div className="text-[10px] tracking-[0.22em] uppercase font-bold mb-1" style={{ color: WINE }}>
                      ★ YES, ADD THIS — +${Number(m.bump_price || 0).toFixed(2)}
                    </div>
                    <div className="font-bold text-base mb-1" style={{ color: "#0F172A" }}>{m.bump_title}</div>
                    {m.bump_description && <div className="text-sm text-foreground/70">{m.bump_description}</div>}
                  </div>
                </div>
              </label>
            )}
            <a href={(bumpChecked && m.bump_cta_url) ? m.bump_cta_url : m.upsell_cta_url}
               target="_blank" rel="noopener noreferrer"
               className="w-full inline-flex items-center justify-center gap-2 px-6 py-5 rounded-md text-lg font-bold uppercase tracking-[0.18em] text-white shadow-lg hover:scale-[1.02] transition"
               style={{ background: accent }}>
              {m.upsell_cta_label || "Order Now"} <ExternalLink className="w-5 h-5"/>
            </a>
            {m.upsell_price > 0 && (
              <div className="text-center mt-2 text-2xl font-serif-display" style={{ color: "#0F172A" }}>
                ${Number(m.upsell_price).toFixed(2)}
                {bumpChecked && m.bump_price > 0 && (
                  <span className="text-sm ml-2" style={{ color: WINE }}>+ ${Number(m.bump_price).toFixed(2)} bump</span>
                )}
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
